import React, { useState } from "react";
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import toast from 'react-hot-toast';
import { getCollageImages } from "../../utils/heroImages";
import { validateForm as validateFormUtil, validateField } from "../../utils/validation";

const Login = () => {
  const [formData, setFormData] = useState({
    emailOrUsername: "",
    password: "",
    rememberMe: false
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const collageImages = getCollageImages().slice(0, 12);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const rules = {
      emailOrUsername: { required: true, label: 'Email or Username' },
      password: { required: true, minLength: 6, label: 'Password' },
    };
    const newErrors = validateFormUtil(formData, rules);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateForgotPassword = () => {
    return validateField('email', forgotPasswordEmail, { required: true, type: 'email', label: 'Email' });
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Login without role - backend will automatically detect user's role from credentials
      const credentials = {
        emailOrUsername: formData.emailOrUsername,
        password: formData.password
      };
      await login(credentials);
      navigate('/dashboard');
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Invalid email/username or password';
      setErrors({
        general: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    const error = validateForgotPassword();
    if (error) {
      setErrors({ forgotPassword: error });
      return;
    }
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setForgotPasswordSent(true);
      setTimeout(() => {
        setShowForgotPassword(false);
        setForgotPasswordSent(false);
        setForgotPasswordEmail("");
      }, 3000);
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-white relative">
      {/* Hero Section with Collage Background */}
      <section
        className="relative w-full overflow-hidden"
        style={{
          height: '100vh',
          minHeight: '600px'
        }}
      >
        {/* Collage Background with Blue Tint - 4 rows x 3 columns = 12 images */}
        <div
          className="absolute inset-0 grid"
          style={{
            gridTemplateColumns: 'repeat(3, 1fr)',
            gridTemplateRows: 'repeat(4, 1fr)',
            gap: 0,
            filter: 'brightness(0.7) saturate(1.2)',
            backgroundColor: '#1E40AF'
          }}
        >
          {collageImages.map((img, index) => (
            <div
              key={index}
              className="relative overflow-hidden"
              style={{
                width: '100%',
                height: '100%',
                opacity: 0.8
              }}
            >
              <img
                src={img}
                alt={`Community member ${index + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
                style={{
                  mixBlendMode: 'multiply'
                }}
              />
            </div>
          ))}
        </div>

        {/* Blue Overlay */}
        <div
          className="absolute inset-0"
          style={{
            backgroundColor: 'rgba(30, 64, 175, 0.4)'
          }}
        ></div>

        {/* Content Overlay */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 py-12">
          {/* Login Title */}
          <h1
            className="text-6xl md:text-7xl font-bold mb-8 text-center"
            style={{
              fontFamily: "'Open Sans', system-ui, sans-serif",
              fontWeight: 700,
              color: '#FFFFFF',
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
              letterSpacing: '2px'
            }}
          >
            Login
          </h1>


          {/* Login Form Card */}
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-8"
            style={{
              backdropFilter: 'blur(10px)',
              backgroundColor: 'rgba(255, 255, 255, 0.95)'
            }}
          >
            <h2
              className="text-2xl font-bold mb-6 text-center"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 700,
                color: '#1F2937'
              }}
            >
              Sign In
            </h2>

            {!showForgotPassword ? (
              <form onSubmit={handleLoginSubmit} className="space-y-6">
                {errors.general && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-600 text-sm">{errors.general}</p>
                  </div>
                )}

                <div>
                  <label htmlFor="emailOrUsername" className="block text-sm font-medium text-gray-700 mb-2">
                    Email or Username
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaEnvelope className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="emailOrUsername"
                      name="emailOrUsername"
                      value={formData.emailOrUsername}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 border rounded-md focus:ring-2 focus:ring-primary-600 focus:border-primary-600 ${errors.emailOrUsername ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="Enter your email or username"
                    />
                  </div>
                  {errors.emailOrUsername && (
                    <p className="text-red-500 text-sm mt-1">{errors.emailOrUsername}</p>
                  )}
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-primary-600 hover:text-primary-700"
                    >
                      Forgot Password?
                    </button>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaLock className="text-gray-400" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-12 py-3 border rounded-md focus:ring-2 focus:ring-primary-600 focus:border-primary-600 ${errors.password ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                  )}
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="rememberMe"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleChange}
                    className="w-4 h-4 text-primary-600 rounded focus:ring-primary-600"
                  />
                  <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-600">
                    Remember me
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full"
                >
                  {isSubmitting ? "Signing In..." : "Sign In"}
                </button>

                <p className="text-center text-sm text-gray-600 pt-6 mt-4 border-t border-gray-200">
                  New Applicant?{" "}
                  <Link
                    to="/register"
                    className="font-semibold text-[#1E40AF] hover:text-[#1E3A8A]"
                  >
                    Register Yourself
                  </Link>
                </p>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-gray-800">Reset Password</h3>
                  <button
                    onClick={() => setShowForgotPassword(false)}
                    className="text-gray-500 hover:text-gray-700 text-sm"
                  >
                    ← Back to login
                  </button>
                </div>

                {forgotPasswordSent ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-2">Email Sent!</h4>
                    <p className="text-gray-600">
                      We've sent password reset instructions to:<br />
                      <span className="font-semibold text-primary-600">{forgotPasswordEmail}</span>
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-gray-600 mb-6">
                      Enter your email address and we'll send you instructions to reset your password.
                    </p>

                    <form onSubmit={handleForgotPasswordSubmit} className="space-y-6">
                      <div>
                        <label htmlFor="forgotPasswordEmail" className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="forgotPasswordEmail"
                          value={forgotPasswordEmail}
                          onChange={(e) => setForgotPasswordEmail(e.target.value)}
                          className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-primary-600 focus:border-primary-600 ${errors.forgotPassword ? 'border-red-500' : 'border-gray-300'
                            }`}
                          placeholder="Enter your registered email"
                        />
                        {errors.forgotPassword && (
                          <p className="text-red-500 text-sm mt-1">{errors.forgotPassword}</p>
                        )}
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-primary w-full"
                      >
                        {isSubmitting ? "Sending..." : "Send Reset Instructions"}
                      </button>
                    </form>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Login;
